import { GraduationCap } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { useWatch } from "react-hook-form";

import {
  CheckboxField,
  ComboboxField,
  CountryField,
  DateField,
  FormRow,
  FormSheet,
  TextareaField,
  TextField
} from "@/components/form";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type {
  EducationPayload,
  ForeignNationalEducationHistoryEntry
} from "../../../../api/types/foreignNationalRecords";
import {
  AgentReadCard,
  FillFromDocuments
} from "../../components/FillFromDocuments";
import { SourceCell } from "../../components/SourceTag";
import {
  ClientTable,
  type ClientTableColumn
} from "../../components/table/ClientTable";
import { ClientTableRow } from "../../components/table/ClientTableRow";
import { ClientTableToolbar } from "../../components/table/ClientTableToolbar";
import {
  DateRangeCell,
  MutedCell,
  SkeletonRow,
  TitleCell
} from "../../components/table/cells";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useRecordCrud } from "../../hooks/useRecordCrud";
import { NAV_ITEMS } from "../../navConfig";
import {
  DEGREE_OPTIONS,
  educationDraftSchema,
  educationDraftToPayload,
  educationFocusDomId,
  educationToDraft,
  emptyEducationDraft,
  sortEducation,
  type EducationDraft
} from "./educationModel";

const getId = (e: ForeignNationalEducationHistoryEntry) =>
  e.education_history_id;

const COLUMNS: ClientTableColumn[] = [
  { id: "school", header: "School" },
  { id: "degree", header: "Degree" },
  { id: "location", header: "Location" },
  { id: "dates", header: "Dates" },
  { id: "source", header: "Source", className: "w-30" }
];

/** Schools and degrees (EB-2, H-1B specialty occupation, N-400 and DS-260 history). */
export const EducationTab: FC = () => {
  const { clientId } = useClientProfile();
  const [search, setSearch] = useState("");

  useEffect(() => setSearch(""), [clientId]);

  const crud = useRecordCrud<
    ForeignNationalEducationHistoryEntry,
    EducationDraft,
    EducationPayload
  >({
    clientId,
    noun: "Education",
    list: ForeignNationalsAPI.listEducationHistoryEntries,
    create: ForeignNationalsAPI.createEducationHistoryEntry,
    update: ForeignNationalsAPI.updateEducationHistoryEntry,
    remove: ForeignNationalsAPI.deleteEducationHistoryEntry,
    getId,
    toDraft: educationToDraft,
    emptyDraft: emptyEducationDraft,
    toPayload: educationDraftToPayload,
    schema: educationDraftSchema,
    sort: sortEducation
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return crud.records
      .map((e) => {
        const degree = [e.degree, e.field_of_study].filter(Boolean).join(", ");
        const location = [e.city, e.country].filter(Boolean).join(", ");
        return {
          entry: e,
          id: getId(e),
          degree: degree || "—",
          location: location || "—",
          haystack: `${e.institution} ${degree} ${location} ${
            e.notes ?? ""
          }`.toLowerCase()
        };
      })
      .filter((r) => !q || r.haystack.includes(q));
  }, [crud.records, search]);

  const isEditing = crud.editor.mode === "edit";
  const isCurrent = useWatch({ control: crud.form.control, name: "isCurrent" });

  useEffect(() => {
    if (isCurrent && crud.form.getValues("endDate")) {
      crud.form.setValue("endDate", "", {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [crud.form, isCurrent]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ClientTableToolbar
        title={NAV_ITEMS.education.label}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search education...",
          ariaLabel: "Search education"
        }}
        onRefresh={() => crud.refresh().catch(() => undefined)}
        isRefreshing={crud.isLoading}
        secondaryActions={<FillFromDocuments />}
        primaryAction={{
          label: "Add Education",
          onClick: () => crud.openCreate()
        }}
      />
      <ClientTable
        ariaLabel="Education history"
        columns={COLUMNS}
        isLoading={crud.isLoading}
        isEmpty={rows.length === 0}
        error={crud.error}
        onRetry={() => crud.refresh().catch(() => undefined)}
        renderSkeletonRow={(i) => (
          <SkeletonRow
            key={i}
            widths={["w-40", "w-32", "w-28", "w-32", "w-16"]}
          />
        )}
        empty={
          crud.records.length === 0
            ? {
                title: "No Education Yet",
                icon: GraduationCap,
                message:
                  "Degrees and schools back up specialty-occupation, EB-2 and naturalization filings.",
                extra: (
                  <AgentReadCard
                    finds="Each school it finds"
                    addLabel="Add Education"
                    onAdd={() => crud.openCreate()}
                  />
                )
              }
            : {
                title: "No Matching Schools",
                icon: GraduationCap,
                message: "Try a different search."
              }
        }
      >
        {rows.map((row) => (
          <ClientTableRow
            key={row.id}
            domId={educationFocusDomId(row.id)}
            onActivate={() => crud.openEdit(row.entry)}
          >
            <TitleCell
              icon={GraduationCap}
              iconClassName={
                row.entry.is_current ? "bg-brand-50 text-brand-600" : undefined
              }
              title={row.entry.institution || "—"}
            />
            <MutedCell>{row.degree}</MutedCell>
            <MutedCell>{row.location}</MutedCell>
            <DateRangeCell
              start={row.entry.start_date}
              end={row.entry.end_date}
              isCurrent={row.entry.is_current}
            />
            <SourceCell source={row.entry.source} />
          </ClientTableRow>
        ))}
      </ClientTable>

      <FormSheet
        form={crud.form}
        open={crud.editor.mode !== "closed"}
        onOpenChange={(open) => {
          if (!open) crud.close();
        }}
        title={isEditing ? "Edit education" : "Add education"}
        description="The school, the degree earned and when."
        onSubmit={crud.submit}
        isEditing={isEditing}
        isSubmitting={crud.isSaving}
        isDeleting={crud.isDeleting}
        onDelete={() => {
          crud.deleteCurrent().catch(() => undefined);
        }}
        deleteTitle="Delete this education entry?"
      >
        <TextField<EducationDraft>
          name="institution"
          label="School"
          required
          placeholder="e.g. University of Lagos"
          autoComplete="organization"
        />
        <FormRow>
          <TextField<EducationDraft>
            name="city"
            label="City"
            placeholder="City"
          />
          <CountryField<EducationDraft> name="country" label="Country" />
        </FormRow>
        <FormRow>
          <ComboboxField<EducationDraft>
            name="degree"
            label="Degree"
            options={DEGREE_OPTIONS}
            placeholder="Select degree"
          />
          <TextField<EducationDraft>
            name="fieldOfStudy"
            label="Field of Study"
            placeholder="e.g. Computer Science"
          />
        </FormRow>
        <FormRow>
          <DateField<EducationDraft> name="startDate" label="Started" />
          <DateField<EducationDraft>
            name="endDate"
            label="Graduated"
            disabled={isCurrent}
            placeholder={isCurrent ? "Still enrolled" : "Pick a date"}
          />
        </FormRow>
        <CheckboxField<EducationDraft>
          name="isCurrent"
          label="The client is enrolled here now"
        />
        <TextareaField<EducationDraft>
          name="notes"
          label="Notes"
          placeholder="Credential evaluation, honors, thesis..."
        />
      </FormSheet>
    </div>
  );
};
